// South African Income Tax Brackets for 2025/2026 tax year
export interface TaxBracket {
  min: number;
  max: number | null; // null means no upper limit
  rate: number;
  fixedTax: number; // Fixed tax up to this bracket
}

export const TAX_BRACKETS: TaxBracket[] = [
  { min: 0, max: 237100, rate: 0.18, fixedTax: 0 },
  { min: 237100, max: 370500, rate: 0.26, fixedTax: 42678 },
  { min: 370500, max: 512800, rate: 0.31, fixedTax: 77362 },
  { min: 512800, max: 673000, rate: 0.36, fixedTax: 121475 },
  { min: 673000, max: 857900, rate: 0.39, fixedTax: 179147 },
  { min: 857900, max: 1817000, rate: 0.41, fixedTax: 251258 },
  { min: 1817000, max: null, rate: 0.45, fixedTax: 644489 },
];

// Tax Rebates by age
export const TAX_REBATES = {
  PRIMARY: 17235,
  SECONDARY: 9444, // 65 and older
  TERTIARY: 3145, // 75 and older
};

// Tax Thresholds
export const TAX_THRESHOLDS = {
  UNDER_65: 95750,
  AGE_65_74: 148217,
  AGE_75_PLUS: 165689,
};

export type AgeGroup = "under65" | "65to74" | "75plus";

export interface TaxCalculationResult {
  grossIncome: number;
  taxBeforeRebate: number;
  taxAfterRebate: number;
  effectiveRate: number;
}

export interface CombinedTaxResult {
  individualTaxTotal: number;
  combinedTax: number;
  additionalTax: number;
  totalIncome: number;
}

/**
 * Calculate tax for a given income
 */
export function calculateTax(
  income: number,
  ageGroup: AgeGroup = "under65"
): TaxCalculationResult {
  if (income < 0) {
    return {
      grossIncome: 0,
      taxBeforeRebate: 0,
      taxAfterRebate: 0,
      effectiveRate: 0,
    };
  }

  // Determine the applicable bracket
  let applicableBracket: TaxBracket | null = null;

  for (const bracket of TAX_BRACKETS) {
    if (
      income >= bracket.min &&
      (bracket.max === null || income <= bracket.max)
    ) {
      applicableBracket = bracket;
      break;
    }
  }

  if (!applicableBracket) {
    applicableBracket = TAX_BRACKETS[TAX_BRACKETS.length - 1];
  }

  // Calculate tax before rebate
  const incomeAboveBracket = income - applicableBracket.min;
  const taxBeforeRebate =
    applicableBracket.fixedTax + incomeAboveBracket * applicableBracket.rate;

  // Apply rebates based on age
  let totalRebate = TAX_REBATES.PRIMARY;
  if (ageGroup === "65to74") {
    totalRebate += TAX_REBATES.SECONDARY;
  } else if (ageGroup === "75plus") {
    totalRebate += TAX_REBATES.SECONDARY + TAX_REBATES.TERTIARY;
  }

  const taxAfterRebate = Math.max(0, taxBeforeRebate - totalRebate);
  const effectiveRate = income > 0 ? (taxAfterRebate / income) * 100 : 0;

  return {
    grossIncome: income,
    taxBeforeRebate,
    taxAfterRebate,
    effectiveRate,
  };
}

/**
 * Calculate tax for multiple income streams
 */
export function calculateCombinedTax(
  incomeStreams: number[],
  ageGroup: AgeGroup = "under65"
): {
  individualResults: TaxCalculationResult[];
  combinedResult: CombinedTaxResult;
} {
  // Calculate tax for each individual income stream
  const individualResults = incomeStreams.map((income) =>
    calculateTax(income, ageGroup)
  );

  // Calculate total individual taxes
  const individualTaxTotal = individualResults.reduce(
    (sum, result) => sum + result.taxAfterRebate,
    0
  );

  // Calculate combined income
  const totalIncome = incomeStreams.reduce((sum, income) => sum + income, 0);

  // Calculate tax on combined income (only one rebate applies)
  const combinedTaxResult = calculateTax(totalIncome, ageGroup);
  const combinedTax = combinedTaxResult.taxAfterRebate;

  // Calculate additional tax due
  const additionalTax = combinedTax - individualTaxTotal;

  return {
    individualResults,
    combinedResult: {
      individualTaxTotal,
      combinedTax,
      additionalTax,
      totalIncome,
    },
  };
}
